<template>
    <div
        ref="filterList"
        class="pb-4 px-4"
        :class="{ 'kiosk-filters': isKioskMode }"
        :style="kioskSizing"
    >
        <div class="container">
            <div class="row">
                <div class="col-md" :class="{ 'my-md-0 my-1': !isKioskMode }" v-for="(lf, index) in filtered" @click="selectFilter(lf)" :key="index">
                    <div class="localized-filter-button">
                        {{ lf.name }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>
    .localized-filter-button {
        font-family: "Poppins", sans-serif;
        background: #1aa8de;
        color: #fff;
        padding: 20px 0;
        border-radius: 5px;
        text-align: center;
        font-size: 14px;
        display: inline-block;
        width: 100%;
        cursor: pointer;
    }

    /* Guest Hub (kiosk) tablet: this list is the whole screen and is pressed
       with a finger, so the choices stack full width and grow to fill the
       space left under the header. The height and font size come from the
       measurement below, which shrinks both when a facility has enough
       filters that full-size buttons would run off the screen. */
    .kiosk-filters .row {
        display: block;
    }

    .kiosk-filters .row > div {
        flex: 0 0 100%;
        width: 100%;
        max-width: 100%;
        padding: 0;
        margin-top: 0;
        margin-bottom: 16px; /* keep in sync with KIOSK_ROW_GAP */
    }

    .kiosk-filters .row > div:last-child {
        margin-bottom: 0;
    }

    .kiosk-filters .localized-filter-button {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: var(--kiosk-filter-height, 120px);
        padding: 8px 16px;
        font-size: var(--kiosk-filter-font, 30px);
        font-weight: 600;
        line-height: 1.25;
        overflow-wrap: break-word;
        touch-action: manipulation;
        user-select: none;
        -webkit-user-select: none;
    }
</style>


<script>
import { isKioskRoute } from "./../kiosk/kiosk_config";

// Bounds for the kiosk fit below. The gap matches the margin between the
// stacked buttons; the height stays large enough to stay a comfortable touch
// target and small enough that a long list still fits without scrolling.
const KIOSK_ROW_GAP = 16;
const KIOSK_BOTTOM_GUTTER = 24;
const KIOSK_MIN_HEIGHT = 56;
const KIOSK_MAX_HEIGHT = 160;
const KIOSK_MIN_FONT = 15;
const KIOSK_MAX_FONT = 32;

export default {
    props: {
        localized_filters: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            // px height of one kiosk button, measured against the screen.
            kiosk_button_height: null,
            kiosk_remeasure_timer: null,
        };
    },
    mounted() {
        if (!this.isKioskMode) return;

        this.$nextTick(this.measureKioskFit);
        // The instruction text above this list is filled in from the facility's
        // custom text, so re-measure once it has settled.
        this.kiosk_remeasure_timer = setTimeout(this.measureKioskFit, 300);

        window.addEventListener('resize', this.measureKioskFit);
        window.addEventListener('orientationchange', this.measureKioskFit);
    },
    beforeDestroy() {
        clearTimeout(this.kiosk_remeasure_timer);
        window.removeEventListener('resize', this.measureKioskFit);
        window.removeEventListener('orientationchange', this.measureKioskFit);
    },
    watch: {
        filtered() {
            if (this.isKioskMode) this.$nextTick(this.measureKioskFit);
        }
    },
    computed: {
      isKioskMode() {
         return isKioskRoute(this.$route);
      },
      filtered() {
         return this.localized_filters.filter((item) => item.status == 1);
      },
      kioskSizing() {
         if (!this.isKioskMode || !this.kiosk_button_height) return null;

         const font = this.clamp(this.kiosk_button_height * 0.26, KIOSK_MIN_FONT, KIOSK_MAX_FONT);

         return {
            '--kiosk-filter-height': `${this.kiosk_button_height}px`,
            '--kiosk-filter-font': `${font}px`,
         };
      }
    },
    methods: {
        async selectFilter(data) {
            this.$emit('select-filter', data);
        },

        // Splits the screen space under the header evenly between the
        // filters, so few filters get big buttons and many filters get
        // smaller ones instead of a list the guest has to scroll.
        measureKioskFit() {
            const list = this.$refs.filterList;
            const count = this.filtered.length;
            if (!list || !count) return;

            const viewport = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
            const top = Math.max(list.getBoundingClientRect().top, 0);
            const available = viewport - top - KIOSK_BOTTOM_GUTTER - (KIOSK_ROW_GAP * (count - 1));

            this.kiosk_button_height = this.clamp(Math.floor(available / count), KIOSK_MIN_HEIGHT, KIOSK_MAX_HEIGHT);
        },

        clamp(value, min, max) {
            return Math.min(Math.max(value, min), max);
        },
    },
};
</script>
